import React from 'react';
import ReactLinkify from 'react-linkify';
import ReactAutoLink from 'react-autolink';
import ReactMarkdown from 'react-markdown';
import { fromJS, is, List, Map } from 'immutable';
import moment from 'moment';
import remarkGfm from 'remark-gfm';
import remarkLinkify from 'remark-linkify';
import EmojiRegex from 'emoji-regex';

import { getBrand } from 'flow/brands/brands';
import CodeBlockWithCopy from 'flow/components/CodeBlockWithCopy';
import Checkbox from 'flow/components/ui-components/Checkbox';
import MessageMention from 'flow/pages/messages/MessageMention';
import ChatMessageType from 'flow/types/chat-message-type';
import {
    extractAbbreviatedName,
    extractName,
    getColorFromInt,
    homeActionUrlPath,
    isEmptyNullOrUndefined,
    listToMap,
} from 'flow/utils';

export const SCROLL_TO_BOTTOM_MARGIN = 150;
export const MAX_DISPLAYED_MESSAGES = 50;
export const MAX_ALLOWED_ATTACHMENT_SIZE = 20 * 1024 * 1024;

// Regexp that matches start of string or white space char, followed by '@' and one or more characters
// that are not '@', and then end of string ($)
// (?:      beginning of non-capturing match
// ^|\\s    beginning of input or whitespace-character
// )        end of non-capturing match
// (        start of match
// @        matches a '@'-character
// [^@]+    matches anything that is not a '@'-character, one or more times
// $        matches the end of input)
export const REGEXP_POSSIBLE_MENTION_STRICT = '(?:^|\\s)(@[^@]+$)';

// Regexp that matches start of string or white space char, followed by '@' and zero or more characters
// that are not '@', and then end of string ($)
// (?:      beginning of non-capturing match
// ^|\\s    beginning of input or whitespace-character
// )        end of non-capturing match
// (        start of match
// @        matches a '@'-character
// [^@]*    matches anything that is not a '@'-character, zero or more times
// $        matches the end of input)
export const REGEXP_POSSIBLE_MENTION = '(?:^|\\s)(@[^@]*$)';

// Regexp that matches start of string or white space char, followed by '/' and zero or more characters
// that are not '/'
// (?:      beginning of non-capturing match
// ^|\\s    beginning of input or whitespace-character
// )        end of non-capturing match
// (        start of match
// /        matches a '/'-character
// [^/]*    matches anything that is not a '/'-character, zero or more times
// )        end of non-capturing match
export const REGEXP_POSSIBLE_PREMADE_ANSWER = '(?:^|\\s)/([^/]+)';

// Regexp that matches start of string or white space char, followed by ':' and two or more characters
// (?:      beginning of non-capturing match
// ^|\\s    beginning of input or whitespace-character
// )        end of non-capturing match
// (        start of match
// :        matches a ':'-character
// [^:]*    matches anything that is not a ':'-character, zero or more times
// $        matches the end of input)
export const REGEXP_POSSIBLE_EMOJI = '(?:^|\\s)(:[^:]*$)';

// This regexp matches one or more whitespace characters
const REGEXP_WHITESPACE_CHARS = /[\s]+/;

const MESSAGE_MAX_VISIBLE_LENGTH = 300;

export const tabKey = 9;
export const enterKey = 13;
export const escaperKey = 27;
export const arrowUpKey = 38;
export const arrowDownKey = 40;

export const shouldSendMessageOnInput = (charCode, event = {}) => {
    return charCode === enterKey && !event.shiftKey && !event.ctrlKey;
};

export const applyLink = (text) => {
    return ReactAutoLink.autolink(text, {
        target: '_blank',
        rel: 'nofollow noopener noreferrer',
        class: 'hyphens-auto',
    });
};

const applyFormat = (format, text, sessionKey) => {
    if (format?.has('chatUser')) {
        return (
            <MessageMention
                name={text}
                chatUserKey={format.getIn(['chatUser', 'key'])}
                sessionKey={sessionKey}
            />
        );
    }
    // Apply link processing for non-mention text when markdown is disabled
    return (
        <ReactLinkify
            properties={{
                target: '_blank',
                rel: 'nofollow noopener noreferrer',
                className: 'text-link',
            }}
        >
            {text}
        </ReactLinkify>
    );
};

/**
 * Non-formatted styling.
 *
 * @param startPosition the start of the 'no format'-styling
 * @param length the length of the 'no format'-styling
 * @return the 'no format'-styling
 */
const createStyling = (startPosition, length) =>
    fromJS({
        startPosition,
        length,
        stylingReference: null,
    });

/**
 * Take the supplied list of stylings and interleave the implicit spacing
 * in format with explicit stylings.
 *
 * This method is _only_ made public (exported) for testability, and should
 * _not_ be used directly.
 *
 * @param stylings a List of formatDTOs
 * @param length the length of the text to be formatted
 * @return a List of format objects. Unformatted will have stylingReference set to 'null'
 * {
 *   startPosition: <number>,
 *   length: <number>,
 *   stylingReference: Object
 * }
 */
export const stylingGapFill = (stylings, length) => {
    // Only one 'no-format'-format if list of stylings is empty
    if (!stylings || stylings.isEmpty()) {
        return List.of(createStyling(0, length));
    } else {
        // Sort stylings by startPosition to ensure correct gap filling
        const sortedStylings = stylings.sortBy((s) => s.get('startPosition'));
        // Initial reducer state.
        // 'startPosition' is a moving index to ensure that there are no gaps in the list of stylings.
        const accumulator = fromJS({ startPosition: 0, data: [] });

        // The resulting list will contain all stylings save for a trailing one
        const gapFilled = sortedStylings
            .reduce((acc, format) => {
                const fmtOffset = format.get('startPosition');
                const fmtLength = format.get('length');
                const accOffset = acc.get('startPosition');

                return acc
                    .set('startPosition', fmtOffset + fmtLength)
                    .update('data', (prev) =>
                        prev
                            .push(
                                createStyling(accOffset, fmtOffset - accOffset),
                            )
                            .push(format),
                    );
            }, accumulator)
            .get('data');

        const last = gapFilled.last();
        const startPosition = last.get('startPosition') + last.get('length');

        // Add a 'no-format'-format at the end and filter out all 0-length stylings.
        return gapFilled
            .push(createStyling(startPosition, length - startPosition))
            .filter((f) => f.get('length') > 0);
    }
};

/**
 * Apply the list of stylings to the supplied text.
 * The stylings are expected to be non-overlapping.
 *
 * @param messageText the format-bare text
 * @param stylings a List of StylingDTOs
 * @param sessionKey string resembling a chatsessionKey
 * @return React component containing formatted text
 */
export const applyStyling = (messageText, stylings = List(), sessionKey) => {
    return applyStylingInternal(messageText, stylings, sessionKey, applyFormat);
};

const applyStylingInternal = (
    messageText,
    stylings,
    sessionKey,
    formatApplicator,
) => {
    const gapFilled = stylingGapFill(stylings, messageText.length);

    const styled = gapFilled.reduce((acc, styling) => {
        const startPosition = styling.get('startPosition');
        const length = styling.get('length');
        const element = formatApplicator(
            styling.get('stylingReference'),
            messageText.substr(startPosition, length),
            sessionKey,
        );

        return acc.push(element);
    }, List());

    return React.Children.toArray(styled.toArray());
};

/**
 * For each text element in the given list it extracts all emojis
 * and creates individual elements out of them, through the help of
 * the given emojiBuilder function.
 *
 * @param messageText  the original list of text (or other parts)
 * @param emojiBuilder a function to create emojis takes a string, returns what ever you want to show
 * @return a list of text with emojis extracted and recreated according to emojiBuilder
 */
export const buildEmojiMessage = (messageText, emojiBuilder) => {
    // Defensive: handle string, array, or React element input
    let text = '';
    if (typeof messageText === 'string') {
        text = messageText;
    } else if (Array.isArray(messageText) && messageText.length > 0) {
        const first = messageText[0];
        if (first && typeof first === 'string') {
            text = first;
        } else if (
            first &&
            first.props &&
            typeof first.props.children === 'string'
        ) {
            text = first.props.children;
        } else if (
            first &&
            first.props &&
            Array.isArray(first.props.children)
        ) {
            text = first.props.children.join('');
        }
    } else if (
        messageText &&
        messageText.props &&
        typeof messageText.props.children === 'string'
    ) {
        text = messageText.props.children;
    }

    const result = [];
    const emojiRegex = EmojiRegex();
    for (
        let match = emojiRegex.exec(text);
        match !== null;
        match = emojiRegex.exec(text)
    ) {
        const emoji = match[0];
        result.push(emojiBuilder(emoji));
    }
    return result;
};

/**
 * Checks if a string is only containing emojis
 * and is very short.
 * @param text a string
 * @return true or false.
 */
export const isShortEmojiMessage = (text) => {
    const regex = EmojiRegex();

    let emojiCount = 0;
    // Since emoji can be made out of multiple code points
    // we rebuild the message to check if it's only made up of emojis
    let emojiText = '';
    for (
        let match = regex.exec(text);
        match !== null;
        match = regex.exec(text)
    ) {
        emojiCount++;
        const emoji = match[0];
        emojiText += emoji;
    }

    // A short emoji message is a message where the text:
    // - is non empty text
    // - only containing emojis (and whitespaces)
    // - is less than four emojis
    const noSpacesText = text.replace(/\s+/g, '');
    return text.length !== 0 && noSpacesText === emojiText && emojiCount < 4;
};

/**
 * Removes variation selector unicode (UVS) characters from a string.
 *
 * Java 8 Webkit does not seem to be able to render the UVS.
 *
 * @param text a string that might contain variation selector characters
 * @return string without UVS
 */
//ToDo investigate if this function is needed in other browsers than Desktop's
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const removeVariationSelector = (text) => {
    let result = '';

    if (text !== undefined && text !== null) {
        const EMOJI_VARIATION_SELECTOR = 'fe0f';

        for (let i = 0; i < text.length; i++) {
            if (text.charCodeAt(i).toString(16) !== EMOJI_VARIATION_SELECTOR) {
                result += text[i];
            }
        }
    }

    return result;
};

/**
 * Check if a chatsession contains a member with a mobileNumber set.
 * If so the member is listed as an 'external contact'.
 *
 * @param session a chatsession
 *
 * @return true if one of the members have mobileNumber
 */
export const isSMSChat = (session) => {
    if (!session) {
        return false;
    }

    return session
        .get('members', List())
        .some((member) => member.has('mobileNumber'));
};

export const isSMSOnlySession = (session) => {
    return isSMSChat(session) && session.get('members', List()).size === 2;
};

/**
 * Escapes special regexp characters in a string
 */
export const escapeRegExp = (text) => {
    return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

/**
 * This method truncates words in such a way that the last word is not cut off.
 *
 * Example.
 *      Let the pipe character '|' denote where the cut happens.
 *      Suppose we're truncating a text with a long word straddling the cutting point:
 *          "[beginning of text] in a really long sentence withARe|allyLongWord at the cutting point [end of text]"
 *      The text this method returns will then end in the following:
 *          "[beginning of text] in a really long sentence withAReallyLongWord ..."
 */
export const truncateText = (text, max_length = MESSAGE_MAX_VISIBLE_LENGTH) => {
    if (!text) {
        return '';
    }
    if (text.length < max_length) {
        return text;
    }
    const remainingWords = text
        .slice(max_length)
        .split(REGEXP_WHITESPACE_CHARS);
    const remainderOfLastWord = remainingWords[0];

    const truncatedText = text.slice(0, max_length).concat(remainderOfLastWord);

    if (remainingWords[1]) {
        // If there are even more words after the last bit of the cut-off word, then add an ellipsis to the end.
        return truncatedText.concat(' ').concat('...');
    } else {
        return truncatedText;
    }
};

/**
 * Help method to inform the message lists if the message should be presented as one item or not.
 *
 * @param prevMessage
 * @param nextMessage
 * @returns {boolean}
 */
const shouldGroupMessages = (prevMessage, nextMessage) => {
    const prevMessageTime = moment(prevMessage.get('sent'));
    const nextMessageTime = moment(nextMessage.get('sent'));

    const prevMessageSender = prevMessage.getIn(['from', 'extensionKey']);
    const nextMessageSender = nextMessage.getIn(['from', 'extensionKey']);

    const prevMessageIsPost =
        ChatMessageType.get(prevMessage.get('type')) === ChatMessageType.POST;
    const nextMessageIsPost =
        ChatMessageType.get(nextMessage.get('type')) === ChatMessageType.POST;

    return (
        !prevMessageIsPost &&
        !nextMessageIsPost &&
        prevMessageSender === nextMessageSender &&
        nextMessageTime.diff(prevMessageTime, 'minutes') < 3
    );
};

/**
 * Group a list of messages according to the criteria shouldGroupMessages.
 *
 * @param groupedByDate list of messages.
 */
export const getMessageGroups = (groupedByDate) => {
    return groupedByDate.map((dateGroup) => {
        return dateGroup.reduce((acc, message) => {
            if (
                acc.size > 0 &&
                shouldGroupMessages(acc.last().last(), message)
            ) {
                return acc.update(-1, (lastList) => lastList.push(message));
            } else {
                return acc.push(List().push(message));
            }
        }, List());
    });
};

export const getWidgetChatSenderName = (message, extensionKey, i18next) => {
    const from = message.get('from');

    if (messageIsFromExtension(message, extensionKey)) {
        return i18next.t('chat.you');
    }
    const senderContact = from.get('contact');
    return extractAbbreviatedName(senderContact, 'Visitor');
};

export const messageIsFromExtension = (message, extensionKey) => {
    if (!message || !message.get('from') || !extensionKey) {
        return false;
    }
    const from = message.get('from');
    return extensionKey === from.get('extensionKey');
};

/**
 * Compares the age of message0 and message1. This function returns a negative
 * number, 0 or a positive number if message0 is older, the same message or
 * newer than message1, respectively.
 *
 * @param message0 the first message
 * @param message1 the second message
 * @return {number}
 */
export const compareChatMessageAge = (message0, message1) => {
    /*
     * Please don't do this stuff, it's really dirty to extract IDs from entity
     * keys.
     *
     * @param key the chatmessage entity key
     * @return the id if the id is found, null if key is null or undefined, or
     *         undefined if the id couldn't be parsed
     */
    const extractChatMessageId = (key) => {
        if (key === undefined || key === null) {
            return null;
        }

        const matches = /^chatmessage-([0-9]+)$/.exec(key);
        return matches !== null ? parseInt(matches[1]) : undefined;
    };

    const message0Id = message0
        ? extractChatMessageId(message0.get('key'))
        : null;
    const message1Id = message1
        ? extractChatMessageId(message1.get('key'))
        : null;

    if (message0Id === undefined || message1Id === undefined) {
        throw new Error(
            'Could not parse message id for one or both of the messages (' +
                message0Id +
                ', ' +
                message1Id +
                ')',
        );
    } else if (message0Id === null && message1Id === null) {
        return 0;
    } else if (message0Id === null) {
        return 0 - message1Id;
    } else if (message1Id === null) {
        return message0Id;
    } else {
        return message0Id - message1Id;
    }
};

export const compareMessageListsLatestAge = (prevMessages, nextMessages) => {
    if (
        !prevMessages ||
        !nextMessages ||
        prevMessages.isEmpty() ||
        nextMessages.isEmpty()
    ) {
        return false;
    }
    const sortedOldMessages = prevMessages.sort(compareChatMessageAge);
    const sortedNewMessages = nextMessages.sort(compareChatMessageAge);

    const lastOldestMessage = sortedOldMessages.last();
    const newOldestMessage = sortedNewMessages.last();

    return compareChatMessageAge(lastOldestMessage, newOldestMessage) > 0;
};

/**
 * This method is for comparing the latest messages in a session's 'latestMessage' field,
 * and the latest message in that session's list of messages.
 *
 * @param chatsState    The state of the substore 'chats' of our Redux-store
 * @param sessionKey    The key of the chatsession we're looking at
 * @returns {*} A blob with some error information
 */
export const compareLatestMessageAndMessageList = (chatsState, sessionKey) => {
    const latestMessageOnSession = chatsState.getIn([
        'sessions',
        sessionKey,
        'latestMessage',
    ]);
    const sessionMessageList = chatsState.getIn(
        ['messages', sessionKey],
        List(),
    );

    const isMissingLatestMessage = !latestMessageOnSession;
    const isMissingMessagesList = sessionMessageList.isEmpty();
    let isKeyMismatch = false;

    const latestKeyOnSession = latestMessageOnSession
        ? latestMessageOnSession.get('key')
        : null;
    const latestMessageOnMessageList =
        sessionMessageList.max(compareChatMessageAge) || null;
    const latestKeyOnMessageList = latestMessageOnMessageList
        ? latestMessageOnMessageList.get('key')
        : null;
    if (latestKeyOnSession !== latestKeyOnMessageList) {
        isKeyMismatch = true;
    }
    return {
        isMissingLatestMessage,
        isMissingMessagesList,
        isKeyMismatch,
        latestKeyOnSession,
        latestKeyOnMessageList,
    };
};

/**
 * Compare two messages (coming either from Top or from the API). If the second
 * argument is newer, return true to signal that we'd like to ask the API for
 * new messages.
 *
 * @param oldLatestMsg  A ChatMessage
 * @param newLatestMsg  Another ChatMessage, that is potentially newer than the first argument.
 * @returns {boolean} true if the first message is older than the second.
 */
export const shouldFetchCompleteMessages = (oldLatestMsg, newLatestMsg) => {
    if (!oldLatestMsg && !newLatestMsg) {
        // No previous messages in this chat, and none received. Don't attempt to fetch more from the API.
        return false;
    }
    return compareChatMessageAge(oldLatestMsg, newLatestMsg) < 0;
};

export const mergeNewChatMessageList = (
    prevMessages,
    nextMessages,
    limitMessageAmount = true,
) => {
    if (List.isList(prevMessages) && !List.isList(nextMessages)) {
        return prevMessages;
    } else if (!List.isList(prevMessages) && List.isList(nextMessages)) {
        return nextMessages;
    } else if (!List.isList(prevMessages) && !List.isList(nextMessages)) {
        return List();
    }
    const prevMessageMap = listToMap(prevMessages, (e) => e.get('key'));
    const nextMessageMap = listToMap(nextMessages, (e) => e.get('key'));
    // Keep the stored object whenever the incoming one says the same thing.
    // Immutable 3 does not compare before writing, so a plain merge would hand
    // back a new reference for every message the response repeats, and every
    // row that reads one by key would re-render for a message it did not care
    // about.
    const mergedMessageMap = prevMessageMap.withMutations((merged) => {
        nextMessageMap.forEach((nextMessage, key) => {
            if (!is(merged.get(key), nextMessage)) {
                merged.set(key, nextMessage);
            }
        });
    });
    // withMutations hands back the very same map when nothing was written, so
    // this is a reference check, not a walk. Returning the stored list keeps
    // its identity too - otherwise every merge produced a new List even when
    // it held exactly the same messages, and everything selecting the list
    // re-rendered for a response that changed nothing.
    if (mergedMessageMap === prevMessageMap) {
        return prevMessages;
    }

    return mergedMessageMap
        .toList()
        .sort(compareChatMessageAge)
        .update((newMessageList) => {
            if (limitMessageAmount) {
                return newMessageList.takeLast(MAX_DISPLAYED_MESSAGES);
            } else {
                return newMessageList;
            }
        });
};

/**
 * True when a fetched page cannot be shown to continue the messages already
 * stored, so that merging the two would leave a hole in the middle of the chat.
 *
 * A page is a window the server hands back whole, and the stored list was one
 * of those once, so each is contiguous in itself. Whether the two meet is a
 * question only the server can answer - the one thing that settles it here is
 * a message they have in common.
 *
 * The join is tested at the newest stored message *below the page*, rather
 * than at whatever is newest overall: a message pushed over Top while the page
 * was in flight sits above the hole, and would otherwise vouch for a
 * continuity it knows nothing about.
 *
 * @param prevMessages  The session's stored messages
 * @param nextMessages  A page from the API, oldest first
 * @param since         The message key the page was anchored on, if any
 */
export const pageLeavesAGap = (prevMessages, nextMessages, since) => {
    // An anchored page begins where the caller said the stored list ends, so
    // it continues it by construction - and shares no message with it, which
    // is the very thing read as a hole below.
    if (since) return false;
    if (!List.isList(prevMessages) || prevMessages.isEmpty()) return false;
    if (!List.isList(nextMessages) || nextMessages.isEmpty()) return false;

    const newestInPage = nextMessages.last();
    const storedBelowPage = prevMessages.filter(
        (message) => compareChatMessageAge(message, newestInPage) < 0,
    );
    // The page reaches back past everything stored, so there is nothing for it
    // to leave behind.
    if (storedBelowPage.isEmpty()) return false;

    const joiningMessage = storedBelowPage.max(compareChatMessageAge);
    return !nextMessages.some(
        (message) => message.get('key') === joiningMessage.get('key'),
    );
};

/**
 * The list without the messages older than the given one.
 *
 * Used to cut a stored list loose from messages it cannot be shown to follow.
 * Deliberately not MAX_DISPLAYED_MESSAGES-based: trimming to a fixed count
 * only clears the stranded messages while the page happens to be exactly that
 * long, and would quietly stop working the day the page size changes.
 *
 * @param messages      A list of messages, oldest first
 * @param oldestToKeep  The message to cut at, kept itself
 */
export const dropMessagesOlderThan = (messages, oldestToKeep) =>
    messages.filter(
        (message) => compareChatMessageAge(message, oldestToKeep) >= 0,
    );

/**
 *  Trim leading and trailing spaces of a string
 * @param text
 */
export const trimTextToEmpty = (text) => {
    if (!String.prototype.trim) {
        return text.replace(/^\s+|\s+$/g, '');
    }
    return text?.trim();
};

/**
 * @param string             String to format
 * @param replacementString  String that will replace whitespaces
 * @returns {string}         A string where whitespaces, if any, are replaced with instances of replacementString
 */

export const replaceWhitespaces = (string, replacementString) => {
    if (isEmptyNullOrUndefined(string)) {
        return '';
    }

    const formatted = trimTextToEmpty(string);
    return formatted.replace(/\s+/g, replacementString);
};

/**
 * Calculates the number of newlines/linebreaks/rows in a message
 *
 * @param {string} message
 * @returns {number}
 */
export const getLinksFromMessage = (message) => {
    if (!Map.isMap(message) || !message.get('message')) {
        return null;
    }
    const messageParts = applyLink(message.get('message'));
    const isLink = (part) =>
        part instanceof Object && part && part.props && part.props.href;
    const messageLink = messageParts.find(isLink);
    if (messageLink) {
        return messageLink.props.href;
    } else {
        return null;
    }
};

/**
 * Find if link is a key in the OpenGraph data map.
 * if argument 'link' is not defined, empty or null, then
 * the function returns true.
 * This is so that the OG fetcher doesn't try to fetch OG data
 * for those values.
 */
export const isInOpenGraphData = (openGraphData, link) => {
    if (!link) {
        return true;
    } else if (!Map.isMap(openGraphData)) {
        return false;
    }
    return openGraphData.has(link);
};

/**
 * Given a list of messages, look through them and return any links
 * that are not already in our OG data.
 */
export const getNewLinksFromMessages = (messages, openGraphData) => {
    if (!messages) {
        return List();
    }
    return messages
        .map(getLinksFromMessage)
        .filter((link) => !isInOpenGraphData(openGraphData, link))
        .toSet()
        .toList();
};

export const receivedNewMessages = (oldMessages, newMessages) => {
    if (oldMessages.isEmpty()) {
        return !newMessages.isEmpty();
    }

    const oldKey = oldMessages.last().get('key');
    const newKey = newMessages.last().get('key');

    return oldKey !== newKey;
};

export const receivedNewFailedMessages = (oldMessages, newMessages) => {
    if (newMessages.isEmpty()) {
        return !oldMessages.isEmpty();
    }
    return receivedNewMessages(oldMessages, newMessages);
};

export const receivedOldMessages = (oldMessages, newMessages) => {
    const gotNewMessages = receivedNewMessages(oldMessages, newMessages);
    const messageListsSizeDiff = oldMessages.size !== newMessages.size;
    const slicedTooLongList = slicedOldMessages(oldMessages, newMessages);
    return !gotNewMessages && messageListsSizeDiff && !slicedTooLongList;
};

const slicedOldMessages = (oldMessages, newMessages) => {
    return oldMessages.size > newMessages.size;
};

export const lastMessageSentByMe = (messages, extensionKey) => {
    const lastMessage = !messages.isEmpty() && messages.last();
    const lastMessageExtensionKey =
        lastMessage && lastMessage.getIn(['from', 'extensionKey']);

    return extensionKey === lastMessageExtensionKey;
};

export const getStyledMessageBody = (
    message,
    sessionKey,
    hasMarkdownEnabled = false,
) => {
    const msg = message.get('message');

    if (!msg) return '';

    // Only use markdown if feature is enabled and message contains markdown characters
    if (hasMarkdownEnabled && /([*_`#[\]-]|\n)/.test(msg)) {
        return (
            <div className="markdown max-w-none whitespace-pre-wrap">
                <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkLinkify]}
                    skipHtml={true}
                    components={{
                        code({ inline, children }) {
                            return (
                                <CodeBlockWithCopy
                                    code={children}
                                    inline={inline}
                                />
                            );
                        },
                        a: ({ children, ...props }) => (
                            <a
                                {...props}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {children}
                            </a>
                        ),
                        p: ({ children }) => (
                            <p className="leading-6 whitespace-pre-wrap text-black">
                                {children}
                            </p>
                        ),

                        input: ({ checked, type, ...props }) => {
                            if (type === 'checkbox') {
                                return (
                                    <Checkbox
                                        checked={!!checked}
                                        disabled={true}
                                        onChange={() => {}}
                                        className="inline-block align-middle"
                                    />
                                );
                            }
                            // fallback for other input types
                            return (
                                <input
                                    type={type}
                                    checked={checked}
                                    {...props}
                                />
                            );
                        },
                    }}
                >
                    {msg}
                </ReactMarkdown>
            </div>
        );
    }
    const styledMessageBody = applyStyling(
        msg,
        message.get('stylings'),
        sessionKey,
    );
    return processShortEmojiMessage(
        message?.get('message'),
        message?.get('key'),
        styledMessageBody,
    );
};

export const processShortEmojiMessage = (
    messageText,
    messageKey,
    styledMessageBody,
) => {
    if (!isShortEmojiMessage(messageText)) return styledMessageBody;

    let emojiCount = 0;
    return buildEmojiMessage(styledMessageBody, (emoji) => (
        <span
            key={'emoji-' + messageKey + emojiCount++}
            style={{ fontSize: '2.5em', lineHeight: '1.25em' }}
        >
            {emoji}
        </span>
    ));
};

export const getStringWithStringInsertedAtPosition = (
    string,
    stringToInsert,
    position,
) => {
    if (!stringToInsert && !string) {
        return '';
    }

    if (!stringToInsert) {
        return string;
    }

    if (!string) {
        return stringToInsert;
    }

    if (isNaN(position) || position < 0) {
        //Position is either not a number or a negative value, adding stringToInsert to the end of string
        return string + stringToInsert;
    }

    return [
        string.slice(0, position),
        stringToInsert,
        string.slice(position),
    ].join('');
};

export const ChatContent = Object.freeze({
    FILES: 'files',
    POSTS: 'posts',
    IMAGES: 'images',
    UNKNOWN: 'unknown',
});

export const getFormattedDateTime = (t, i18next) => {
    if (!t) {
        return '-';
    }
    const time = moment(t);
    const startOfDay = moment(t).startOf('day');
    const today = moment();

    const isWithinWeek = startOfDay.diff(today.startOf('day'), 'days') > -7;
    const isWithinYear = startOfDay.diff(today.startOf('day'), 'years') === 0;

    if (isWithinWeek) {
        return time.locale(i18next.language).format('ddd HH:mm');
    } else if (isWithinYear) {
        return time.locale(i18next.language).format('D MMM HH:mm');
    } else {
        return time.locale(i18next.language).format('D MMM YYYY HH:mm');
    }
};

export const fileSizeConverter = (bytes, decimals = 1) => {
    if (!bytes) return undefined;
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const ChatAttachments = Object.freeze({
    DOC: 'doc',
    KEYNOTE: 'keynote',
    MOVIE: 'movie',
    NUMBERS: 'numbers',
    PAGES: 'pages',
    PDF: 'pdf',
    PHOTO: 'photo',
    PPT: 'ppt',
    SKETCH: 'sketch',
    XLS: 'xls',
    ZIP: 'zip',
    DEFAULT: 'default',
    UNKNOWN: 'unknown',
});

export const getAttachmentTypeFromExtension = (fileName) => {
    if (!fileName) {
        return ChatAttachments.UNKNOWN;
    }

    const filename = fileName.toLowerCase();

    if (filename.match(/.*\.doc/i) || filename.match(/.*\.docx/i)) {
        return ChatAttachments.DOC;
    } else if (filename.match(/.*\.key/i)) {
        return ChatAttachments.KEYNOTE;
    } else if (filename.match(/.*\.mp4/i) || filename.match(/.*\.mov/i)) {
        return ChatAttachments.MOVIE;
    } else if (filename.match(/.*\.numbers/i)) {
        return ChatAttachments.NUMBERS;
    } else if (filename.match(/.*\.pages/i)) {
        return ChatAttachments.PAGES;
    } else if (filename.match(/.*\.pdf/i)) {
        return ChatAttachments.PDF;
    } else if (
        filename.match(/.*\.ico/i) ||
        filename.match(/.*\.jpg/i) ||
        filename.match(/.*\.png/i) ||
        filename.match(/.*\.svg/i) ||
        filename.match(/.*\.icns/i) ||
        filename.match(/.*\.jpeg/i) ||
        filename.match(/.*\.heic/i)
    ) {
        return ChatAttachments.PHOTO;
    } else if (filename.match(/.*\.ppt/i) || filename.match(/.*\.pptx/i)) {
        return ChatAttachments.PPT;
    } else if (filename.match(/.*\.sketch/i)) {
        return ChatAttachments.SKETCH;
    } else if (filename.match(/.*\.xls/i) || filename.match(/.*\.xlsx/i)) {
        return ChatAttachments.XLS;
    } else if (filename.match(/.*\.zip/i)) {
        return ChatAttachments.ZIP;
    } else if (filename.match(/^([^\\]*)\.(\w+)$/)) {
        return ChatAttachments.DEFAULT;
    } else {
        return ChatAttachments.UNKNOWN;
    }
};

export const getImageFromChatAttachment = (
    attachment = ChatAttachments.UNKNOWN,
) => {
    switch (attachment) {
        case ChatAttachments.MOVIE:
            return 'movie';
        case ChatAttachments.PHOTO:
            return 'image';
        default:
            return 'file';
    }
};

/**
 * Generic hash function that should be used by all apps.
 */
const hashCode = (ext) => {
    let k = 32952;

    for (let i = 0; i < ext.length; i++) {
        k = (k << 5) + k + ext.charCodeAt(i);
    }

    return k;
};

const getColorFromFileExtension = (fileName) => {
    const fileSplit = fileName.match(/^([^\\]*)\.(\w+)$/);

    const extension = fileSplit[fileSplit.length - 1];
    const hash = hashCode(extension);

    return getColorFromInt(hash);
};

export const getColorFromChatAttachment = (
    attachment = ChatAttachments.UNKNOWN,
    fileName,
) => {
    switch (attachment) {
        case ChatAttachments.DOC:
        case ChatAttachments.KEYNOTE:
            return getBrand().theme.palette.flavors.flavor6;
        case ChatAttachments.MOVIE:
            return getBrand().theme.palette.flavors.flavor8;
        case ChatAttachments.NUMBERS:
        case ChatAttachments.XLS:
            return getBrand().theme.palette.flavors.green;
        case ChatAttachments.PAGES:
        case ChatAttachments.ZIP:
            return getBrand().theme.palette.flavors.flavor2;
        case ChatAttachments.PDF:
        case ChatAttachments.PPT:
            return getBrand().theme.palette.flavors.red;
        case ChatAttachments.PHOTO:
        case ChatAttachments.SKETCH:
            return getBrand().theme.palette.flavors.flavor1;
        case ChatAttachments.UNKNOWN:
            return getBrand().theme.palette.greyscale.greyLight;
        default:
            return getColorFromFileExtension(fileName);
    }
};

export const getMuteLengthEnum = (
    activeProfileEndTime,
    i18next,
    statusText,
) => {
    const TimeLengthEnum = {
        ONE_MINUTE: { value: '1', label: `1 ${i18next.t('duration.hour')}` },
        EIGHT_HOURS: { value: '8', label: `8 ${i18next.t('duration.hours')}` },
        TWENTY_FOUR_HOURS: {
            value: '24',
            label: `24 ${i18next.t('duration.hours')}`,
        },
    };

    if (activeProfileEndTime) {
        TimeLengthEnum.UNTIL_PROFILE_ACTIVE = {
            value: 'profile',
            label: i18next.t('Chat.Mute.UntilProfile', {
                ProfileStatus: statusText,
            }),
        };
    }

    TimeLengthEnum.UNTIL_FURTHER_NOTICE = {
        value: null,
        label: i18next.t('Chat.Mute.UntilForever'),
    };

    return TimeLengthEnum;
};

export const getMutedUntil = (chatSession) =>
    chatSession.getIn(['chatMuted', 'mutedUntil']);

export const getProxyImageUrl = (imageUrl) => {
    return homeActionUrlPath(
        `/api/internal/opengraph/image?url=${encodeURIComponent(imageUrl)}`,
    );
};

/**
 * Go through the whole text backwards and return a list of mentions
 */
export const createMentionDTOsFromText = (text, extensions) => {
    let mentions = List();
    let currentMentionPosition = text.length;
    for (;;) {
        // Find a possible mention
        const regexp = new RegExp(REGEXP_POSSIBLE_MENTION_STRICT);
        const regexpResult = regexp.exec(text);
        if (regexpResult == null) {
            return mentions;
        }
        const possibleMention = regexpResult[1];
        currentMentionPosition -= possibleMention.length;
        // Go through the possible mention, one more character at a time
        for (let i = 2; i <= possibleMention.length; i++) {
            const filterText = possibleMention.substring(1, i);
            const filteredExtensions = extensions.filter((extension) =>
                mentionFilter(extension, filterText),
            );
            if (filteredExtensions.size > 1) {
                // If there are more than two possible matches, continue by adding another character
            } else if (filteredExtensions.size === 0) {
                // If there are no matches, break and try to find another possible mention
                break;
            } else {
                const extension = filteredExtensions.first();
                const name = extractName(extension.get('contact'), '');
                if (name.length !== filterText.length) {
                    continue;
                }
                if (name !== filterText) {
                    break;
                }

                const key = extension.get('chatUserKey');
                const length = name.length + 1;
                const mentionDTO = {
                    startPosition: currentMentionPosition,
                    length: length,
                    stylingReference: {
                        stylingType: 'chatuser',
                        chatUser: { key: key },
                    },
                };
                mentions = mentions.push(fromJS(mentionDTO));
            }
        }
        text = text.substring(0, currentMentionPosition);
    }
};

const mentionFilter = (extension, filterText) => {
    if (!extension) return false;
    const firstName = extension.getIn(['contact', 'firstName'], '');
    const lastName = extension.getIn(['contact', 'lastName'], '');
    const fullName = firstName + ' ' + lastName;
    return new RegExp('(^|\\s)' + escapeRegExp(filterText), 'i').test(fullName);
};

export const isTwoPersonSMSChat = (attachment, session, extensions) => {
    if (attachment || !session) {
        return false;
    }
    const members = session
        .get('members', List())
        .filter((member) => member.get('contact'));
    const memberExtensionKeys = members
        .map((member) => member.get('extensionKey'))
        .toSet();

    const filteredExtensions = extensions.filter(
        (extension) =>
            memberExtensionKeys.contains(extension.get('key')) &&
            extensionHasMobile(extension),
    );

    return filteredExtensions.size === 2;
};

export const extensionHasMobile = (extension) => {
    const contact = extension.get('contact');
    return contact && contact.get('mobileNumber');
};

export const getChatSessionMemberExtensions = (
    extensions,
    chatSession,
    loggedInEntityKey,
) => {
    const sessionMembersKeys = chatSession
        .get('members', List())
        .map((member) => member.get('extensionKey'));

    return extensions.filter(
        (ext) =>
            sessionMembersKeys.contains(ext.get('key')) &&
            ext.get('key') !== loggedInEntityKey,
        Map(),
    );
};

export const copyImage = (url) => {
    // Use the Canvas API, which seems to offer the best cross-browser chance of this
    // working out. We want the full original image to be copied, so create a throwaway
    // Image tag to obtain that.
    const img = new Image();
    img.src = url;
    img.onload = async () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height);
        canvas.toBlob(async (blob) => {
            await navigator.clipboard.write([
                new ClipboardItem({ [blob.type]: blob }),
            ]);
        });
    };
};
